import {
  useMemo, useState, useCallback
} from 'react'
import dynamic from 'next/dynamic'
import Head from 'components/Head'
import Navbar from 'components/Navbar'
import Footer from 'components/Footer'
import PromosProvider from 'components/Promos'
import company from 'config/company.json'
import { initHeaders } from 'utils/request/headers'
import { withErrorStaticProps, Props as StaticProps } from 'utils/request/ssg'
import { loadTranslations } from 'utils/request/i18n'
import {
  NavigationProvider,
  useCompanySchema,
  getDestinationHub,
  getFilterLimits,
  parseBoatFiltersQueryString,
  listBoats,
  listMarkers,
  useLanguage,
  useLocaleState,
  OrganicLandingSearchProvider,
  OrganicLandingSearch,
  OrganicLandingTopMessage,
  OrganicLandingContent,
  withMapContext
} from '@sailogy/shipwright'
import {
  DestinationHub, BoatFilters, FilterLimits, BoatListed, ListMeta, MarkerSimple
} from '@sailogy/types'
import { i18nPath } from 'i18n/landing_organic'

const LandingEnquiryModal = dynamic(
  () => import('@sailogy/containers/Enquiry/Landing/Modal/Timer'),
  { ssr: false }
)

interface Props {
  destination: DestinationHub
  filters: Partial<BoatFilters>
  limits: FilterLimits
  boats: BoatListed[]
  meta: ListMeta
  markers: MarkerSimple[]
}

export const serializeRelatedLinks = (destination: DestinationHub) => ({
  ...destination,
  // Append id to slug to maintain SEO consistency
  related_links: destination.related_links
    .map((d: any) => ({ ...d, slug: `${d.slug}-${d.id}` }))
} as DestinationHub)


const DestinationHubPage: React.FC<Props> = ({
  filters: defaultFilters, limits, boats, meta, markers,
  destination: ssrDestination
}) => {
  const [language] = useLanguage()
  const structuredData = useCompanySchema(company)
  const [destination, setDestination] = useLocaleState<DestinationHub>(
    ssrDestination, async (signal: AbortSignal) => (
      getDestinationHub(ssrDestination.slug[language], { signal })
        .then(serializeRelatedLinks)
    )
  )
  const filters = (typeof window !== 'undefined' && window.location.search)
    ? parseBoatFiltersQueryString(window.location.search.slice(1))
    : defaultFilters
  const [loading, setLoading] = useState<boolean>(false)
  // WARN: Sitemap has slugs with id value in the end
  const destinationSlugs = useMemo(() => (
    Object.keys(destination.slug).reduce((acc, lang) => ({
      // Slug should be null if page is not allowed
      // Append id to slug to maintain SEO consistency
      ...acc, [lang]: destination.slug[lang] && `${destination.slug[lang]}-${destination.id}`
    }), {})
  ), [destination])

  const handleChange = useCallback(async (slug: string) => {
    // Load new destination data
    setLoading(true)
    // Remove id from slug
    const newDestination = await getDestinationHub(slug.replace(/-\d+$/, ''))
      .then(serializeRelatedLinks)
    // Update values
    setDestination(newDestination)
    setLoading(false)
  }, [setDestination])

  return (
    <NavigationProvider path="/seo_destinations/:slug" vars={{ slug: destinationSlugs }}>
      <Head
        cleanTitle
        title={destination.meta_title.replace('{SITE_NAME}', company.name)}
        description={destination.meta_description.replace('{SITE_NAME}', company.name)}
        image={destination.image}
        schema={structuredData}
        noIndex={filters !== defaultFilters}
      />
      <Navbar />
      <PromosProvider>
        <OrganicLandingSearchProvider filters={filters}>
          <OrganicLandingSearch
            limits={limits}
            boats={boats}
            meta={meta}
            markers={markers}
          >
            <OrganicLandingTopMessage
              destination={loading ? null : destination}
              onChange={handleChange}
            />
          </OrganicLandingSearch>
        </OrganicLandingSearchProvider>
        {!loading && (
          <OrganicLandingContent
            content={destination.seo_paragraphs}
            onChange={handleChange}
          />
        )}
      </PromosProvider>
      <LandingEnquiryModal timer={10000} />
      <Footer />
    </NavigationProvider>
  )
}

// Build SSG last pages for each language
export { getRequiredStaticPaths as getStaticPaths } from 'utils/request/ssg'

export const getStaticProps = withErrorStaticProps<Props>(async (ctx) => {
  // WARN: Sitemap has slugs with id value in the end
  const slug = (ctx.params!.slug as string).replace(/-\d+$/, '')
  const [i18n, destination, limits] = await Promise.all([
    loadTranslations(ctx.locale!, i18nPath),
    getDestinationHub(slug, initHeaders(ctx))
      .then(serializeRelatedLinks),
    getFilterLimits().catch(() => ({ }))
  ])
  if (destination == null) {
    return { props: ctx.params, notFound: true }
  }
  // Prefetch boats and markers based
  const filters: Partial<BoatFilters> = {
    search_where: destination.destination,
    weeks_count: 1
  }
  if (destination.skipper != null) filters.skipper = destination.skipper
  if (destination.boat_type != null) {
    filters.boat_types = [destination.boat_type]
  }

  const [{ results: boats, ...res }, markers] = await Promise.all([
    listBoats(filters, initHeaders(ctx))
      .catch(() => ({ results: [], count: 0 })),
    listMarkers(filters, initHeaders(ctx)).catch(() => [])
  ])
  const meta: ListMeta = {
    count: res.count,
    nextPage: ('next' in res && res.next != null) ? 2 : null!,
    previousPage: null!
  }

  return {
    props: {
      ...ctx.params,
      i18n,
      i18nPath,
      destination,
      filters,
      limits,
      boats,
      meta,
      markers
    } as StaticProps<Props>,
    // Invalidate when list is empty
    revalidate: boats.length === 0 ? 1 : 86400 // 24h
  }
})

export default withMapContext(DestinationHubPage)
